import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../services/auth';

export default function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [remaining, setRemaining] = useState(null);
  const [lockedSeconds, setLockedSeconds] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [remember, setRemember] = useState(true);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotStep, setForgotStep] = useState('request');
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotToken, setForgotToken] = useState('');
  const [forgotNewPass, setForgotNewPass] = useState('');
  const [forgotMsg, setForgotMsg] = useState('');
  const [forgotError, setForgotError] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    fetch('/api/config/logo')
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.logo) setLogoUrl(d.logo); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (lockedSeconds > 0) {
      setLockedSeconds(lockedSeconds);
      timerRef.current = setInterval(() => {
        setLockedSeconds(prev => {
          if (prev <= 1) { clearInterval(timerRef.current); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timerRef.current);
    }
  }, [lockedSeconds]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setRemaining(null);
    setLockedSeconds(null);
    setLoading(true);
    try {
      await login(username, password, 'synoti', remember);
      window.location.hash = 'dashboard';
    } catch (err) {
      setError(err.message);
      if (err.remaining !== null && err.remaining !== undefined) setRemaining(err.remaining);
      if (err.lockedSeconds !== null && err.lockedSeconds !== undefined) setLockedSeconds(err.lockedSeconds);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotRequest = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMsg('');
    setForgotLoading(true);
    try {
      const res = await fetch('/api/v1/auth/request-password-reset', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail, tenant_slug: 'synoti' }),
      });
      if (res.ok) {
        setForgotStep('confirm');
        setForgotMsg('If the email exists, a reset token has been generated. Enter the token and a new password.');
      } else {
        const d = await res.json().catch(() => ({}));
        setForgotError(d.detail || 'Request failed');
      }
    } catch (e) {
      setForgotError('Network error');
    }
    setForgotLoading(false);
  };

  const handleForgotReset = async (e) => {
    e.preventDefault();
    setForgotError('');
    setForgotMsg('');
    setForgotLoading(true);
    try {
      const res = await fetch('/api/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: forgotToken, new_password: forgotNewPass }),
      });
      if (res.ok) {
        setForgotMsg('Password reset successfully. Sign in with your new password.');
        setForgotStep('request');
        setForgotEmail('');
        setForgotToken('');
        setForgotNewPass('');
      } else {
        const d = await res.json().catch(() => ({}));
        setForgotError(d.detail || 'Reset failed');
      }
    } catch (e) {
      setForgotError('Network error');
    }
    setForgotLoading(false);
  };

  const backToLogin = () => {
    setForgotMode(false);
    setForgotStep('request');
    setForgotError('');
    setForgotMsg('');
  };

  return (
    <div className="synoti-ui" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div className="login-wrap" style={{ width: '100%', maxWidth: 420 }}>
        <div className="login-brand" style={{ justifyContent: 'center', marginBottom: 8 }}>
          {logoUrl ? (
            <img src={logoUrl} alt="SYNOTI" className="brand-logo" style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'contain' }} />
          ) : (
              <div className="brand-logo" style={{ width: 34, height: 34, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--grad-brand)' }}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 20 }}>S</span>
            </div>
          )}
          <b>SYNOTI</b>
        </div>
        <p style={{ textAlign: 'center', color: 'var(--text-3)', fontSize: 13, marginBottom: 24 }}>AIOPS Platform</p>

        <div className="login-card">
          <h1 style={{ fontSize: 22, color: 'var(--text-1)' }}>Sign In</h1>

          {error && (
            <div style={{
              padding: '10px 14px', borderRadius: 10, fontSize: 13,
              background: lockedSeconds > 0 ? 'rgba(248,113,113,0.20)' : remaining !== null ? 'rgba(251,191,36,0.10)' : 'rgba(248,113,113,0.10)',
              color: lockedSeconds > 0 ? 'var(--danger)' : remaining !== null ? 'var(--warning)' : 'var(--danger)',
              border: `1px solid ${lockedSeconds > 0 ? 'rgba(248,113,113,0.5)' : remaining !== null ? 'rgba(251,191,36,0.3)' : 'rgba(248,113,113,0.3)'}`,
            }}>
              <div style={{ fontWeight: 500 }}>{error}</div>
              {remaining !== null && remaining > 0 && (
                <div style={{ fontSize: 12, marginTop: 4, opacity: 0.8 }}>
                  Attempts remaining: <span style={{ fontWeight: 700 }}>{remaining}</span>
                </div>
              )}
              {lockedSeconds > 0 && (
                <div style={{ fontSize: 12, marginTop: 4, fontFamily: 'var(--mono)', opacity: 0.8 }}>
                  Unlocks in {Math.floor(lockedSeconds / 60)}:{String(lockedSeconds % 60).padStart(2, '0')}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="field" style={{ marginBottom: 0 }}>
              <label>Username</label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="admin"
                autoComplete="username"
                required
                style={{ width: '100%', padding: '9px 13px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--text-1)', fontFamily: 'inherit', fontSize: 13, outline: 'none' }}
              />
            </div>

            <div className="field" style={{ marginBottom: 0 }}>
              <label>Password</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  style={{ width: '100%', padding: '9px 40px 9px 13px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--text-1)', fontFamily: 'inherit', fontSize: 13, outline: 'none' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer' }}
                >
                  {showPassword ? (
                    <svg style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg style={{ width: 18, height: 18 }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
              <label className="flex items-center gap-2" style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-2)' }}>
                 <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} style={{ accentColor: 'var(--brand-pink)' }} />
                 Remember me
                </label>
                <button type="button" onClick={() => setForgotMode(true)} style={{ background: 'none', border: 'none', color: 'var(--brand-pink)', cursor: 'pointer', fontSize: 12 }}>
                Forgot password?
              </button>
            </div>

              <button
               type="submit"
               className="btn-brand"
               disabled={loading || !username || !password}
               style={{ padding: '12px 0', borderRadius: 12, fontWeight: 600, opacity: (loading || !username || !password) ? 0.5 : 1 }}
              >
              {loading ? (
                <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                  <svg className="animate-spin" style={{ width: 20, height: 20 }} viewBox="0 0 24 24"><circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" /><path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
                  Signing in...
                </span>
              ) : 'Sign In'}
            </button>
          </form>

          {forgotMode && (
            <div style={{ marginTop: 16, padding: '16px 0 0', borderTop: '1px solid var(--glass-border)' }}>
              {forgotStep === 'request' ? (
                <form onSubmit={handleForgotRequest} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <p style={{ fontSize: 12, color: 'var(--text-3)' }}>Enter your account email to request a password reset.</p>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Email</label>
                    <input type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} placeholder="admin@synoti.com" required
                      style={{ width: '100%', padding: '9px 13px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--text-1)', fontFamily: 'inherit', fontSize: 13, outline: 'none' }} />
                  </div>
                    <button type="submit" className="btn-brand" disabled={forgotLoading || !forgotEmail}
                     style={{ padding: '10px 0', borderRadius: 10, fontWeight: 600, opacity: (forgotLoading || !forgotEmail) ? 0.5 : 1 }}>
                    {forgotLoading ? 'Sending...' : 'Send reset token'}
                  </button>
                </form>
              ) : (
                <form onSubmit={handleForgotReset} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <p style={{ fontSize: 12, color: 'var(--text-3)' }}>{forgotMsg}</p>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>Reset token</label>
                    <input type="text" value={forgotToken} onChange={(e) => setForgotToken(e.target.value)} placeholder="Token from email" required
                      style={{ width: '100%', padding: '9px 13px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--text-1)', fontFamily: 'inherit', fontSize: 13, outline: 'none' }} />
                  </div>
                  <div className="field" style={{ marginBottom: 0 }}>
                    <label>New password</label>
                    <input type="password" value={forgotNewPass} onChange={(e) => setForgotNewPass(e.target.value)} placeholder="New password" required
                      style={{ width: '100%', padding: '9px 13px', borderRadius: 9, border: '1px solid var(--border-strong)', background: 'var(--surface)', color: 'var(--text-1)', fontFamily: 'inherit', fontSize: 13, outline: 'none' }} />
                  </div>
                     <button type="submit" className="btn-brand" disabled={forgotLoading || !forgotToken || !forgotNewPass}
                     style={{ padding: '10px 0', borderRadius: 10, fontWeight: 600, opacity: (forgotLoading || !forgotToken || !forgotNewPass) ? 0.5 : 1 }}>
                    {forgotLoading ? 'Resetting...' : 'Reset password'}
                  </button>
                </form>
              )}
              {forgotError && <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: 'rgba(248,113,113,0.10)', color: 'var(--danger)' }}>{forgotError}</div>}
              {forgotStep === 'confirm' && !forgotError && forgotMsg && <div style={{ marginTop: 10, padding: '8px 12px', borderRadius: 8, fontSize: 12, background: 'rgba(52,211,153,0.10)', color: 'var(--success)' }}>{forgotMsg}</div>}
              <button type="button" onClick={backToLogin} style={{ marginTop: 12, background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 12 }}>
                ← Back to sign in
              </button>
            </div>
          )}

          <div className="login-meta">
            <span>Powered by: ALYPOD LLC</span>
            <span>&copy; {new Date().getFullYear()} SYNOTI AIOPS Platform</span>
          </div>
        </div>
      </div>
    </div>
  );
}
